import { Box, MenuItem } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import TestRequestPurpose from "../../../api/models/test-request/TestRequestPurpose";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import TestRequestApi from "../../../api/services/TestRequestApi";
import UserInfoApi, { UserReferenceResponse } from "../../../api/services/UserInfoApi";
import CustomAutocomplete from "../../../components/Autocomplete/CustomAutocomplete";
import CustomButton from "../../../components/Button/CustomButton";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import StyledTooltip from "../../../components/Wrapper/StyledTooltip";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { useLaboratories } from "../../../contexts/LaboratoriesContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import { UserContext } from "../../../contexts/UserContext";
import useEnv from "../../../hooks/env";
import useDebounce from "../../../hooks/useDebounce.hook";

type CSCLaboratory = TestRequestRaw.Schemas.CSCLaboratory;

export interface AssignDialogProps {
  testRequestId: string;
  currentLaboratory: CSCLaboratory | false;
  open: boolean;
  onClose: () => void;
  onSuccess: (testReqData: TestRequestResponse) => void;
  preSave: () => Promise<TestRequestResponse | void>;
  testRequestPurpose: TestRequestPurpose;
}

const AssignDialog: React.FC<AssignDialogProps> = (props) => {
  const { currentLaboratory, open, onClose, onSuccess, preSave, testRequestId, testRequestPurpose } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const userInfoApi = new UserInfoApi(api);
  const { user } = useContext(UserContext);
  const currentUser = user
    ? {
        groups: [],
        userId: user.userId || "",
        userName: user.userName || "",
        userEmail: user.userEmail || "",
        laboratories: user.laboratories || [],
        assignedLaboratories: [],
        managedLaboratories: [],
      }
    : null;
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const [laboratory, setLaboratory] = useState<CSCLaboratory | false>(currentLaboratory);
  const [assignee, setAssignee] = useState<UserReferenceResponse | null>(currentUser);
  const [labUsers, setLabUsers] = useState<UserReferenceResponse[]>();
  const [query, setQuery] = useState<string>("");
  const {
    assignToTeamMember,
    assignToSelf,
    description: descriptionPermissions,
  } = React.useContext(TestRequestPermissionContext);
  const debouncedQuery = useDebounce(query, 300);
  const { labs, labsLoading } = useLaboratories(testRequestPurpose);

  const assignToUser = (testReqId: string) =>
    testRequestApi.assignTestRequestToUser(testReqId, laboratory, assignee?.userId || "").then((value) => {
      showSnackbar(`Test request successfully assigned to ${assignee?.userName}`, "success");
      return value.data;
    });

  const doAssign = (testReqId: string) =>
    descriptionPermissions?.editDescription ? preSave().then(() => assignToUser(testReqId)) : assignToUser(testReqId);

  const onSubmit = (testReqId: string) => {
    doAssign(testReqId)
      .then((res) => {
        onSuccess(res);
        onClose();
      })
      .catch((error) => {
        handleHttpError(error);
        onClose();
      });
  };

  useEffect(() => {
    if (assignToTeamMember && open) {
      userInfoApi
        .getAssignableUsers(testRequestId, laboratory, debouncedQuery)
        .then(({ data }) => {
          if (currentUser && (!laboratory || currentUser.laboratories.includes(laboratory))) {
            const filteredUsersArr = data.data.filter((item) => item.userId !== currentUser.userId);
            filteredUsersArr.unshift(currentUser);
            return filteredUsersArr;
          }
          return data.data;
        })
        .then((users) => setLabUsers(users))
        .catch(handleHttpError);
    }
  }, [debouncedQuery, open, laboratory]);

  useEffect(() => {
    if (open) {
      // reset fields on open dialog
      setLaboratory(currentLaboratory);
      setAssignee(currentUser);
    }
  }, [open]);

  useEffect(() => {
    if (laboratory && !assignee?.laboratories.includes(laboratory)) {
      setAssignee(null);
    }
  }, [laboratory]);

  const sameLab = currentLaboratory === laboratory;
  const selfSelected = assignee?.userId === currentUser?.userId;
  const selfSelectedButNotPermitted = selfSelected && !assignToSelf;
  const noAssigneeSelected = !assignee;
  const helpText =
    (noAssigneeSelected && "No assignee selected") || (selfSelectedButNotPermitted && "You are already assigned") || "";
  const title =
    (selfSelected ? "ASSIGN YOURSELF TO THE TEST REQUEST" : "ASSIGN TEST REQUEST TO COLLEAGUE") +
    (sameLab ? "" : " AND ADJUST LAB");
  const text = `${
    sameLab ? "" : "The test request will be assigned to another lab. "
  }The assigned user will be responsible to evaluate and possibly execute the test request.`;

  return (
    <CustomDialog maxWidth="sm" open={open} onClose={onClose}>
      <h2 style={{ paddingRight: 24 }}>{title}</h2>
      <p>{text}</p>
      {laboratory && (
        <>
          <CustomDropdown
            labelText="CSC Laboratory"
            onChange={(event) => setLaboratory(event.target.value as CSCLaboratory)}
            value={laboratory}
          >
            {labsLoading ? (
              <MenuItem value={laboratory}>Loading...</MenuItem>
            ) : (
              labs.map((lab) => (
                <MenuItem key={lab.key} value={lab.key}>
                  {lab.name}
                </MenuItem>
              ))
            )}
          </CustomDropdown>
          <Box height={12} />
        </>
      )}
      <CustomAutocomplete
        autoFocus
        label="Lab User"
        onChange={(e, val) => {
          setAssignee(val);
        }}
        onInputChange={(e, val) => {
          setQuery(val);
        }}
        options={labUsers || []}
        optionKey="userName"
        value={assignee}
      />
      <Box display="flex" justifyContent="flex-end" marginTop="26px">
        <StyledTooltip title={helpText}>
          <Box>
            {/* <Box /> needed to display tooltip with nested <CustomButton /> */}
            <CustomButton
              disabled={noAssigneeSelected || selfSelectedButNotPermitted}
              text={selfSelected ? "Assign to me" : "Assign"}
              primary
              onClick={() => onSubmit(testRequestId)}
            />
          </Box>
        </StyledTooltip>
      </Box>
    </CustomDialog>
  );
};

export default AssignDialog;
